import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import queryString from "query-string";
import { useSnackbar } from "notistack";
import { AUTH_CONFIG } from "../../common/configs/auth.config";
import {
  ENVIRONMENT,
  SNACKBAR_TYPE,
  SSO_BASE_PATH,
} from "../../common/constants/constants";
import { getEnvironment } from "../../common/utils";
import { generatePKCE, generateState } from "../../common/utils/pkceUtils";

const SSOLoginScreen = () => {
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const EnvFlag = getEnvironment();

  // Get registration URL based on environment
  const getRegistrationUrl = () => {
    return EnvFlag === ENVIRONMENT.PROD
      ? "https://landolakesinc.my.site.com/ems/EMSSelfAppRegistrationPage"
      : "https://landolakesinc--uat.sandbox.my.site.com/ems/EMSSelfAppRegistrationPage";
  };

  // Get redirect URI based on environment
  const getRedirectUri = () => {
    if (EnvFlag === ENVIRONMENT.PROD) {
      return AUTH_CONFIG.EXT_REDIRECT_URI_PROD;
    } else if (EnvFlag === ENVIRONMENT.QA) {
      return AUTH_CONFIG.EXT_REDIRECT_URI_QA;
    } else if (EnvFlag === ENVIRONMENT.DEV) {
      return AUTH_CONFIG.EXT_REDIRECT_URI_DEV;
    }
    return AUTH_CONFIG.EXT_REDIRECT_URI_LOCAL;
  };

  // Get help URL based on environment
  const getHelpUrl = () => {
    const redirectUri = getRedirectUri();
    const baseUrls = {
      "https://dairydev.addieinsights.com/ext/auth":
        "https://dairydev.addieinsights.com/sign-in-help",
      "https://dairystage.addieinsights.com/ext/auth":
        "https://dairystage.addieinsights.com/sign-in-help",
      "http://localhost:3000/ext/auth": "http://localhost:3000/sign-in-help",
    };
    return (
      baseUrls[redirectUri] || "https://dairy.addieinsights.com/sign-in-help"
    );
  };

  useEffect(() => {
    // Check for businessLinkId in query params and show error
    const params = queryString.parse(location.search);
    const businessLinkId = params.businessLinkId;

    if (businessLinkId) {
      const registrationUrl =
        getRegistrationUrl() + "?BusinessLinkId=" + businessLinkId;
      const message = `This BusinessLink account (${businessLinkId}) does not have access to Addie. If you would like access, please `;
      setErrorMessage(message);
      setShowError(true);
      setUsername(businessLinkId);
    }
  }, [location.search, EnvFlag]);

  // Handle SSO (Internal) Login
  const handleSSOLogin = async () => {
    try {
      const { codeVerifier, codeChallenge } = await generatePKCE();
      sessionStorage.setItem("pkce_code_verifier", codeVerifier);

      // Generate state parameter for CSRF protection
      const state = generateState();
      sessionStorage.setItem("oauth_state", state);

      const redirectUri = getRedirectUri().replace("/ext/auth", "/auth");
      const clientId = AUTH_CONFIG.CLIENT_ID;
      const ssoBaseUrl = SSO_BASE_PATH();

      const url = `${ssoBaseUrl}/as/authorization.oauth2?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&scope=openid%20profile%20email&code_challenge=${codeChallenge}&code_challenge_method=S256&state=${state}`;
      window.location.href = url;
    } catch (error) {
      console.error("Error generating PKCE:", error);
      enqueueSnackbar("Error initiating SSO login", {
        anchorOrigin: { vertical: "top", horizontal: "center" },
        variant: SNACKBAR_TYPE.ERROR,
      });
    }
  };

  // Handle External Login (username/password) using PKCE flow
  const handleExternalLogin = async (e) => {
    e.preventDefault();

    if (!username || !password) {
      enqueueSnackbar("Please enter username and password", {
        anchorOrigin: { vertical: "top", horizontal: "center" },
        variant: SNACKBAR_TYPE.WARNING,
      });
      return;
    }

    setIsLoading(true);

    try {
      // Generate PKCE challenge
      const { codeVerifier, codeChallenge } = await generatePKCE();
      sessionStorage.setItem("pkce_code_verifier", codeVerifier);

      // Generate state parameter for CSRF protection
      const state = generateState();
      sessionStorage.setItem("oauth_state", state);

      const redirectUri = getRedirectUri();
      const clientId = AUTH_CONFIG.EXT_CLIENT_ID;
      const ssoBaseUrl = SSO_BASE_PATH();

      // Build authorization URL with PKCE and state
      const authUrl = `${ssoBaseUrl}/as/authorization.oauth2?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&scope=openid%20profile%20email&code_challenge=${codeChallenge}&code_challenge_method=S256&state=${state}`;

      // Create a hidden form to submit credentials directly to PingFederate
      // This mimics the original HTML form behavior
      const form = document.createElement("form");
      form.method = "POST";
      form.action = authUrl;
      form.style.display = "none";

      // Add username field (pf.username is PingFederate's field name)
      const usernameField = document.createElement("input");
      usernameField.type = "hidden";
      usernameField.name = "pf.username";
      usernameField.value = username;
      form.appendChild(usernameField);

      // Add password field
      const passwordField = document.createElement("input");
      passwordField.type = "hidden";
      passwordField.name = "pf.pass";
      passwordField.value = password;
      form.appendChild(passwordField);

      // Add the form to the page and submit it
      document.body.appendChild(form);
      form.submit();
    } catch (error) {
      console.error("Error during login:", error);
      setIsLoading(false);
      enqueueSnackbar("Login failed. Please check your credentials.", {
        anchorOrigin: { vertical: "top", horizontal: "center" },
        variant: SNACKBAR_TYPE.ERROR,
      });
    }
  };

  // Handle help link
  const handleHelpClick = (e) => {
    e.preventDefault();
    const helpUrl = getHelpUrl();
    window.open(helpUrl, "_blank");
  };

  // Handle registration link
  const handleRegistrationClick = (e) => {
    e.preventDefault();
    const registrationUrl = getRegistrationUrl();
    window.open(registrationUrl, "_blank");
  };

  return (
    <section className="external-page-container">
      <section className="external-content-container">
        <div className="content">
          <div className="logo-container">
            <i className="logo"></i>
          </div>
          <h1>Sign In</h1>
          <p>
            Addie uses the same Username and Password associated with your
            BusinessLink account.
          </p>

          {showError && (
            <div className="invalidLoginMsg-box" style={{ display: "block" }}>
              <p className="invalidLoginMsg">
                {errorMessage}
                <a
                  href={getRegistrationUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  request access here
                </a>
                .
              </p>
            </div>
          )}

          <form
            onSubmit={handleExternalLogin}
            autoComplete="off"
            className="external-login-form"
          >
            <input
              type="text"
              className="login-text-field"
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoCorrect="off"
              autoCapitalize="off"
            />
            <input
              type="password"
              className="login-text-field"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button type="submit" className="button" disabled={isLoading}>
              {isLoading ? "Signing In..." : "Sign In"}
            </button>
            <button
              type="button"
              className="button outline sso-button"
              onClick={handleSSOLogin}
            >
              Sign In with SSO
            </button>
          </form>

          <div className="help-link-container">
            <a href="#" onClick={handleHelpClick} className="left-link">
              Need help?
            </a>
            <a
              href="#"
              onClick={handleRegistrationClick}
              className="right-link"
            >
              New User Registration
            </a>
          </div>
        </div>
      </section>
      <section className="external-supportive-container">
        <div className="supportive-content"></div>
      </section>
    </section>
  );
};

export default SSOLoginScreen;
